/* import __COLOCATED_TEMPLATE__ from './schedule-content-update-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type ArticleContent from 'embercom/models/articles/article-content';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import moment from 'moment-timezone';
import type ScheduledContentUpdateService from 'embercom/services/scheduled-content-update-service';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';
import type ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';
import ScheduledContentUpdateModel from 'embercom/models/content-service/scheduled-content-update';

interface Args {
  activeContent: ArticleContent;
  scheduleActiveContentUpdate: (params: {
    mode: 'publish' | 'unpublish';
    scheduledDate: Date;
  }) => Promise<void>;
  onClose: () => void;
  mode?: 'publish' | 'unpublish';
}

export default class ScheduleContentUpdateModal extends Component<Args> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare permissionsService: any;
  @service declare scheduledContentUpdateService: ScheduledContentUpdateService;

  @tracked scheduledPublishDate = this.initialScheduledPublishDate;
  @tracked today = new Date();

  get initialScheduledPublishDate() {
    return moment
      .tz(this.appService.app.timezone)
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  get scheduledContentUpdates() {
    return (this.args.activeContent.scheduledContentUpdates?.toArray() || []).filter(
      (update: ScheduledContentUpdate) => update.status === ScheduledContentUpdateStatus.PENDING,
    );
  }

  get pendingScheduledContentUpdates() {
    return this.scheduledContentUpdates.map((update) => {
      let result = ScheduledContentUpdateModel.getScheduleDateAndTime(
        update,
        this.appService.app.timezone,
        this.intl,
      );

      if (!result) {
        return {
          ...update,
          formattedDate: '',
          formattedTime: '',
        };
      }

      let [formattedDate, formattedTime] = result;
      return {
        ...update,
        formattedDate,
        formattedTime,
      };
    });
  }

  get hasScheduledContentUpdates() {
    return this.scheduledContentUpdates.length > 0;
  }

  get isUnpublishMode() {
    return this.args.mode === 'unpublish';
  }

  get scheduleHeaderTitle() {
    if (this.isUnpublishMode) {
      if (this.hasScheduledContentUpdates) {
        return this.intl.t(
          'knowledge-hub.content-editor.article-content.schedule-modal.update-unpublish-schedule-header',
        );
      }
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.schedule-modal.create-unpublish-schedule-header',
      );
    }

    if (this.hasScheduledContentUpdates) {
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.schedule-modal.update-schedule-header',
      );
    }
    return this.intl.t(
      'knowledge-hub.content-editor.article-content.schedule-modal.create-schedule-header',
    );
  }

  get scheduleButtonLabel() {
    if (this.isUnpublishMode) {
      if (this.hasScheduledContentUpdates) {
        return this.intl.t(
          'knowledge-hub.content-editor.article-content.schedule-modal.update-unpublish-schedule',
        );
      }
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.schedule-modal.create-unpublish-schedule',
      );
    }

    if (this.hasScheduledContentUpdates) {
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.schedule-modal.update-schedule',
      );
    }
    return this.intl.t(
      'knowledge-hub.content-editor.article-content.schedule-modal.create-schedule',
    );
  }

  get scheduleDescriptionText() {
    if (this.isUnpublishMode) {
      if (this.hasScheduledContentUpdates) {
        return this.intl.t(
          'knowledge-hub.content-editor.article-content.schedule-modal.update-when-to-unpublish-article',
        );
      }
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.schedule-modal.schedule-when-to-unpublish-article',
      );
    }

    if (this.hasScheduledContentUpdates) {
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.schedule-modal.update-when-to-publish-article',
      );
    }
    return this.intl.t(
      'knowledge-hub.content-editor.article-content.schedule-modal.schedule-when-to-publish-article',
    );
  }

  @action
  onScheduledPublishDateChange(date: Date) {
    this.scheduledPublishDate = date;
  }

  @action
  cancel() {
    this.args.onClose();
  }

  @action
  scheduleActiveContentUpdate() {
    let mode = this.args.mode || 'publish';
    this.args.scheduleActiveContentUpdate({ mode, scheduledDate: this.scheduledPublishDate });
    this.args.onClose();
  }

  @action
  async cancelScheduledPublish() {
    try {
      await this.permissionsService.checkPermission(
        this.args.activeContent.requiredPublishPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }

    try {
      await this.scheduledContentUpdateService.cancel({
        entityId: this.args.activeContent.id,
        entityType: this.args.activeContent.entityType,
      });

      // Update the status in the store to trigger immediate reactivity
      this.scheduledContentUpdates.forEach((update) => {
        update.status = ScheduledContentUpdateStatus.CANCELLED;
      });

      let confirmationMessage = this.isUnpublishMode
        ? this.intl.t(
            'knowledge-hub.content-editor.article-content.schedule-unpublish-modal.cancel-schedule-success',
          )
        : this.intl.t(
            'knowledge-hub.content-editor.article-content.schedule-modal.cancel-schedule-success',
          );
      this.notificationsService.notifyConfirmation(confirmationMessage);
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          'knowledge-hub.content-editor.article-content.schedule-modal.cancel-schedule-error',
        ),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::ScheduleContentUpdateModal': typeof ScheduleContentUpdateModal;
    'knowledge-hub/content-editor/article-content/schedule-content-update-modal': typeof ScheduleContentUpdateModal;
  }
}
